/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { ViewModel } from '../../../../../core/viewmodel/ViewModel';
import { EventTypes } from '../../../../../models/application/EventModel';
import { Message } from '../../../../../types/Message';
import { ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import { CourseService } from '../../../../../services/CourseService';
import { ChatService } from '../../../../../services/ChatService';
import { AuthStore } from '../../../../../stores/application/AuthStore';
import { SnackbarStore } from '../../../../../stores/application/SnackbarStore';
import { EventStore } from '../../../../../stores/application/EventStore';
import { StoreProvider } from '../../../../../stores/StoreProvider';
import * as H from 'history';
import { ChatStore } from '../../../../../stores/components/ChatStore';
import { MaterialMenu } from '../../../../../models/material/MaterialMenu';
import { socket } from '../../../../../config/web-sockets';
import { getActiveRoomId, getIsoDateWithoutTime } from '../../../../../utils/chat';
import { Student } from '../../../../../types/Student';
import { role } from '../../../../../consts/chat';
import { publicChatKey } from '../../../../../models/components/ChatModel';

export class ChatBoxViewModel extends ViewModel {
	public menu: MaterialMenu = new MaterialMenu();
	private routerParams: { courseId: string } = useParams();

	private courseService: CourseService;
	private chatService: ChatService;

	private authStore: AuthStore;
	private snackbarStore: SnackbarStore;
	private eventStore: EventStore;
	private chatStore: ChatStore;

	constructor(storeProvider: StoreProvider, history: H.History) {
		super(storeProvider, history);
		this.courseService = storeProvider.courseService;
		this.chatService = storeProvider.chatService;
		this.authStore = storeProvider.authStore;
		this.snackbarStore = storeProvider.snackbarStore;
		this.eventStore = storeProvider.eventStore;
		this.chatStore = storeProvider.chatStore;
		this.reactOnMessage();
		this.reactOnDeleteMessage();
	}

	private reactOnMessage(): void {
		socket.on('message', (message: Message) => {
			if (!this.chatStore.getMessageIdentifiers().includes(message.identifier)) {
				this.saveMessage(message);
			}
			if (this.getActiveChat() === message.room && this.chatStore.getUnreadMessagesAmount(message.room) > 0) {
				this.setMessagesAsRead(message.room);
			}
		});
	}

	private saveMessage(message: Message): void {
		const allMessages = this.chatStore.getMessages();
		const messages = allMessages[message.room] ?? [];

		if (messages.length === 0) {
			message.newDay = true;
		} else if (messages.length > 1) {
			message.newDay =
				getIsoDateWithoutTime(messages[messages.length - 1].identifier) < getIsoDateWithoutTime(message.identifier);
		}
		messages.push(message);
		this.chatStore.setMessages(message.room, messages);
		this.chatStore.addMessageIdentifier(message.identifier);
	}

	private reactOnDeleteMessage(): void {
		socket.on('deleteMessage', (data: { identifier: string }) => {
			this.deleteMessage(data.identifier);
		});
	}

	public subscribeToChatRoomEvent(): EventTypes[] {
		return this.eventStore.subscribeToEvent(EventTypes.CHATROOM_CHANGE, this);
	}

	private setMessagesAsRead(key: string): void {
		const authToken = this.authStore.getAuthToken();

		this.chatStore.resetUnreadMessages(key);
		this.chatService.setMessagesAsRead(authToken, key).catch(() => {
			this.snackbarStore.showError('chat.set_as_read_error');
		});
	}

	public getStudents(): Student[] {
		return this.chatStore.getStudents();
	}

	public getActiveChat(): string {
		const activeChat = this.chatStore.getActiveChat();
		return activeChat === publicChatKey ? this.routerParams.courseId : activeChat;
	}

	public areMessagesFetching(): boolean {
		return this.chatStore.areMessagesBeingFetched();
	}

	public getMessages(key: string): Message[] {
		const messages: { [key: string]: Message[] } = this.chatStore.getMessages();

		return messages[key] ?? [];
	}

	public getMessage(): string {
		return this.chatStore.getMessage();
	}

	public changeMessage(event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void {
		this.chatStore.setMessage(event.target.value);
	}

	public sendMessage(): void {
		const userId = this.authStore.getUserId();
		const room = getActiveRoomId(this.routerParams, this.chatStore.getActiveChat());
		const message = this.getMessage();
		if (message) {
			socket.emit('sendMessage', { role: role, userId, message, room }, (err: Error) => {
				if (err) {
					this.snackbarStore.showError('chat.send_message_error');
				}
			});
			this.chatStore.setMessage('');
		}
	}

	public deleteMessage(identifier: string): void {
		const activeChat = this.getActiveChat();
		const allMessages = this.chatStore.getMessages();
		const tempMessages = allMessages[activeChat];
		const messages = tempMessages.filter((message) => message.identifier !== identifier);

		this.chatStore.setMessages(activeChat, messages);
	}
}
